import React, { useRef, useState, useEffect, useMemo } from "react";
import Button from "@mui/material/Button";
import MGridForm from "components/Shared/MGridForm";
import {
  DATA_TYPE_DROPDOWN,
  DATA_TYPE_STRING,
  DATA_TYPE_PHONE_NUMBER,
  DATA_TYPE_DATE,
  DATA_TYPE_TIME,
  DATA_TYPE_MULTI_LINE_STRING,
  DATA_TYPE_EMAIL,
} from "typeConstants/dataTypes";
import { contactPersonTypes, contactEffortResultTypes } from "typeConstants/contactEfforts";
import validator from "components/Shared/MGridFormDefinitions/validator";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import { ContactEffortsSettingsDto } from "interfaces/Mueller.Business.Models";

/** This is similar to ContactEffortsDto but uses strings for some ids to allow these to be used with select controls and adds contactTime property for separate date and time controls */
export interface CallLogEntryForm {
  contactEffortsId: Number;
  controlIdNumber: Number;
  phoneNumber: string;
  contactDate: string;
  contactTime: Date;
  contactPersonTypeCode: string;
  spokeWith: string;
  resultCode: string;
  notes: string;
}

interface AddEditCallLogDialogProps {
  /** Call log data to edit */
  data: CallLogEntryForm;
  /** Function to call to hide the dialog */
  onCancel: () => void;
  /** Function to call when add/edit finished that will be passed the form data */
  onSave: (data: CallLogEntryForm) => void;
  /** Call log settings */
  settings: ContactEffortsSettingsDto;
  /** Available case contacts (from PNX) */
  contacts?: ContactEffortsContactsDto;
}

const EntryType = {
  PHONE: "PHONE",
  EMAIL: "EMAIL",
};

const getInitialEntryType = (props: AddEditCallLogDialogProps) =>
  props?.data?.phoneNumber && props?.data?.phoneNumber?.indexOf("@") >= 0 ? EntryType.EMAIL : EntryType.PHONE;

const AddEditCallLogDialog = (props: AddEditCallLogDialogProps) => {
  const CREATE_NEW = "__CREATE_NEW__";

  const [callLogData, setCallLogData] = useState<any>({
    ...props.data,
    contactSelection: props.data?.phoneNumber ? props.data.phoneNumber : CREATE_NEW,
  });
  const getResultTypesForPersonType = (personTypeCode: string) => {
    if (!props.settings?.personTypes?.length) {
      return contactEffortResultTypes;
    }
    if (personTypeCode) {
      const availResults = props.settings.personTypes.filter((s) => s.key === personTypeCode)[0]?.possibleResultIDs;
      if (availResults) {
        return contactEffortResultTypes.filter((s) => availResults.some((p) => p === s.value));
      }
    }
    return [];
  };

  const [entryType, setEntryType] = useState<string>(getInitialEntryType(props));
  const [personType, setPersonType] = useState<string>(props.data?.contactPersonTypeCode || null);
  const [availResultTypes, setAvailResultTypes] = useState(() =>
    getResultTypesForPersonType(props.data?.contactPersonTypeCode),
  );
  const formControlMethods = useRef({}); // TODO Add type when MGridForm has interfaces

  const setFormFieldValue = (field: string, value: any) => {
    if (formControlMethods.current?.setFieldModified) {
      formControlMethods.current.setFieldModified(field, value);
    }
  };

  useEffect(() => {
    // Reset form when editing a different call log entry
    setEntryType(getInitialEntryType(props));
    setCallLogData({
      ...props.data,
      contactSelection: props.data?.phoneNumber ? props.data.phoneNumber : CREATE_NEW,
    });
  }, [props.data]);

  const filteredContacts = useMemo(() => {
    const selectedPersonType = callLogData?.contactPersonTypeCode;
    const allContacts = props.contacts?.contacts ?? [];

    return allContacts
      .filter((c) => c && c.isValid)
      .filter((c) => !!c.value)
      .filter((c) => !!selectedPersonType && c.type === selectedPersonType)
      .filter((c) => (entryType === EntryType.EMAIL ? `${c.value}`.includes("@") : !`${c.value}`.includes("@")));
  }, [props.contacts, callLogData?.contactPersonTypeCode, entryType]);

  const contactPersonTypeLabelByValue = useMemo(() => {
    return (contactPersonTypes ?? []).reduce((acc, option) => {
      acc[option.value] = option.label;
      return acc;
    }, {});
  }, []);

  const contactOptions = useMemo(() => {
    const label = entryType === EntryType.EMAIL ? "Create New (enter email)" : "Create New (enter number)";
    return [
      { label, value: CREATE_NEW },
      ...filteredContacts.map((c) => ({
        label: `${c.value}${c.name || c.type ? ` (${c.name ?? contactPersonTypeLabelByValue[c.type] ?? c.type})` : ""}`,
        value: c.value,
      })),
    ];
  }, [filteredContacts, entryType, contactPersonTypeLabelByValue]);

  useEffect(() => {
    // If the current selection is not available anymore (person type/method changed), fall back to Create New
    const selection = callLogData?.contactSelection;
    if (!selection || selection === CREATE_NEW) return;
    const stillValid = filteredContacts.some((c) => c.value === selection);
    if (!stillValid) {
      setCallLogData((prev) => ({
        ...prev,
        contactSelection: CREATE_NEW,
        phoneNumber: prev.phoneNumber === prev.contactSelection ? "" : prev.phoneNumber,
      }));
      setFormFieldValue("contactSelection", CREATE_NEW);
      if (callLogData?.phoneNumber === callLogData?.contactSelection) {
        setFormFieldValue("phoneNumber", "");
      }
    }
  }, [filteredContacts, callLogData?.contactSelection]);

  const handleSave = () => {
    if (formControlMethods.current.isFormValid()) {
      const payload = { ...(callLogData || {}) };
      delete payload.contactSelection;
      props.onSave(payload);
    }
  };

  const handleFormChange = (updatedObject: any, source?: string, dataType?: string, value?: any) => {
    if (!props.settings?.personTypes?.length) {
      setAvailResultTypes(contactEffortResultTypes);
    } else {
      let newPersonType = updatedObject.contactPersonTypeCode;
      if (newPersonType && newPersonType !== personType) {
        let availResults =
          props.settings.personTypes.filter((s) => s.key === newPersonType)[0]?.possibleResultIDs ?? [];
        let resultTypes = contactEffortResultTypes.filter((s) => availResults.some((p) => p === s.value));
        setAvailResultTypes(resultTypes);
        setPersonType(newPersonType);
      }
    }

    // Switching Contact Person should reset the contacted phone/email selection/input
    if (source === "contactPersonTypeCode") {
      updatedObject = {
        ...updatedObject,
        contactSelection: CREATE_NEW,
        phoneNumber: "",
      };
      setFormFieldValue("contactSelection", CREATE_NEW);
      setFormFieldValue("phoneNumber", "");
    }

    // Map dropdown selection to phoneNumber/email value
    if (source === "contactSelection") {
      const newSelection = value ?? CREATE_NEW;
      const nextPhoneNumber = newSelection === CREATE_NEW ? "" : newSelection;

      updatedObject.phoneNumber = nextPhoneNumber;
      updatedObject.contactSelection = newSelection === CREATE_NEW ? CREATE_NEW : newSelection;

      // IMPORTANT: update the underlying MGridForm model so later edits don't overwrite this
      setFormFieldValue("phoneNumber", nextPhoneNumber);
    }

    setCallLogData({ ...updatedObject });
  };

  const formDefinition = useMemo(() => {
    let mGridFormDefinition: any[] = [
      [
        {
          dataType: DATA_TYPE_DATE,
          label: "Date of Contact",
          source: "contactDate",
          hasValidationError: validator.required,
        },
      ],
      [
        {
          dataType: DATA_TYPE_TIME,
          label: "Time of Contact",
          source: "contactTime",
          hasValidationError: validator.required,
        },
      ],
      [{ dataType: "none", label: "", style: { className: "call-log-form-spacer", borderBottom: 0 } }],
      [
        {
          dataType: DATA_TYPE_DROPDOWN,
          label: "Contact person",
          source: "contactPersonTypeCode",
          dataTypeOptions: { options: contactPersonTypes },
          hasValidationError: validator.required,
        },
      ],
    ];

    const showManualEntry = callLogData?.contactSelection === CREATE_NEW;

    mGridFormDefinition.push([
      {
        dataType: DATA_TYPE_DROPDOWN,
        label: entryType === EntryType.EMAIL ? "Email contacted" : "Number contacted",
        source: "contactSelection",
        key: `contactSelection-${entryType}-${callLogData?.contactPersonTypeCode ?? ""}-${callLogData?.contactSelection ?? CREATE_NEW}`,
        dataTypeOptions: { options: contactOptions },
        hasValidationError: validator.required,
      },
    ]);

    if (entryType === EntryType.PHONE) {
      mGridFormDefinition.push([
        {
          dataType: DATA_TYPE_PHONE_NUMBER,
          label: "Enter number",
          source: "phoneNumber",
          enabled: () => showManualEntry,
          visible: () => showManualEntry,
          hasValidationError: validator.combineValidators(validator.required, validator.phoneNumber),
        },
      ]);
    } else {
      mGridFormDefinition.push([
        {
          // Note that we still use the phoneNumber field to store email address
          dataType: DATA_TYPE_EMAIL,
          label: "Enter email",
          source: "phoneNumber",
          enabled: () => showManualEntry,
          visible: () => showManualEntry,
          hasValidationError: validator.combineValidators(validator.required, validator.emailAddress),
        },
      ]);
    }

    mGridFormDefinition.push(
      [
        {
          dataType: DATA_TYPE_STRING,
          label: "Spoke with",
          source: "spokeWith",
          hasValidationError: validator.required,
        },
      ],
      [
        {
          dataType: DATA_TYPE_DROPDOWN,
          label: "Result",
          source: "resultCode",
          dataTypeOptions: { options: availResultTypes },
          hasValidationError: validator.required,
        },
      ],
      [{ dataType: DATA_TYPE_MULTI_LINE_STRING, label: "Notes", source: "notes" }],
    );

    return mGridFormDefinition;
  }, [entryType, personType, callLogData?.contactSelection, contactOptions]);

  return (
    <>
      <div>Select Contact Method:</div>
      <FormControl component="fieldset">
        <RadioGroup
          aria-label="Contact method"
          name="contactMethod"
          value={entryType}
          onChange={(e) => {
            const newEntryType = e.target.value;
            setEntryType(newEntryType);

            setCallLogData((prev) => ({
              ...prev,
              contactSelection: CREATE_NEW,
              phoneNumber: "",
            }));

            // Keep MGridForm's internal model in sync so subsequent edits don't restore an old value
            setFormFieldValue("contactSelection", CREATE_NEW);
            setFormFieldValue("phoneNumber", "");
          }}
        >
          <FormControlLabel value={EntryType.PHONE} control={<Radio />} label="Phone" />
          <FormControlLabel value={EntryType.EMAIL} control={<Radio />} label="Email" />
        </RadioGroup>
      </FormControl>

      <MGridForm
        key={props.data?.contactEffortsId != null ? `${props.data.contactEffortsId}` : "new"}
        width={500}
        data={callLogData}
        onChange={handleFormChange}
        formDefinition={formDefinition}
        portalTarget={() => document.querySelector(".add-edit-call-log-dialog")}
        getControlMethods={(cm) => {
          formControlMethods.current = cm;
        }}
        webform
      />
      <div className="button-row">
        <Button key={"call-log-cancel-delete"} onClick={props.onCancel}>
          Cancel
        </Button>
        <Button key={"call-log-confirm-delete"} onClick={handleSave}>
          Save
        </Button>
      </div>
    </>
  );
};

export default AddEditCallLogDialog;
